/* Search-engine ping widget shown on Admin → Stats tab.
   Pings IndexNow (Bing, Yandex, Seznam, DDG) with every important URL.
   Google is not pinged because Google deprecated the sitemap-ping URL in
   June 2023 — for Google, keep publishing consistently and re-submit the
   sitemap in Search Console when you add many URLs at once. */
import { useEffect, useState } from "react";
import { Rocket, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtAgo(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return iso;
  }
}

export default function SearchPingCard() {
  const [status, setStatus] = useState({ last_pinged_at: null, last_count: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Latest ping response — kept so the "Copy top URLs for GSC" button
  // can offer the admin a prioritised list after a successful ping.
  const [lastResult, setLastResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ping-status");
      setStatus(data || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const ping = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ping-search-engines");
      const extras = (data.cookbooks || data.recipes)
        ? ` — ${data.cookbooks || 0} cookbooks + ${data.recipes || 0} recipes`
        : "";
      toast.success(`Pushed ${data.count} URLs to ${data.targets.join(", ")}${extras}`);
      setStatus({ last_pinged_at: data.pinged_at, last_count: data.count });
      setLastResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ping failed. See logs.");
    } finally {
      setBusy(false);
    }
  };

  const copyTopUrls = async () => {
    if (!lastResult?.top_urls_for_gsc?.length) return;
    try {
      await navigator.clipboard.writeText(lastResult.top_urls_for_gsc.join("\n"));
      toast.success(`${lastResult.top_urls_for_gsc.length} URLs copied — paste one at a time into GSC's "Request indexing".`);
    } catch { toast.error("Copy failed — select the list manually."); }
  };

  return (
    <div className="card-recipe" data-testid="admin-ping-card">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0">
          <p className="overline mb-1">Search engine ping</p>
          <p className="text-sm text-rf-ink-soft">
            One-click IndexNow to Bing, Yandex, Seznam &amp; DuckDuckGo. Runs automatically on publish/update — use this button after bulk edits to force an immediate re-crawl.
          </p>
        </div>
        <Rocket size={28} weight="duotone" className="text-rf-orange shrink-0" />
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-1.5 text-xs text-rf-ink-soft">
          {status.last_pinged_at ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : <Clock size={14} className="text-rf-ink-soft" />}
          <span>Last ping: <b className="text-rf-ink">{fmtAgo(status.last_pinged_at)}</b></span>
        </div>
        {status.last_count > 0 && (
          <div className="text-xs text-rf-ink-soft">
            URLs submitted: <b className="text-rf-ink">{status.last_count}</b>
          </div>
        )}
        {loading && <div className="text-xs text-rf-ink-soft">refreshing…</div>}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid="admin-ping-now"
          onClick={ping}
          disabled={busy}
          className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy ? "Pinging…" : "Ping search engines now"}
        </button>
        {lastResult?.top_urls_for_gsc?.length > 0 && (
          <button
            type="button"
            onClick={copyTopUrls}
            data-testid="admin-ping-copy-gsc"
            className="btn-outline-ink !py-2 !px-3 text-xs"
            title="Copy a prioritised URL list so you can paste rows into GSC's Request Indexing form"
          >
            Copy top {lastResult.top_urls_for_gsc.length} URLs for GSC
          </button>
        )}
        <a
          href="https://search.google.com/search-console"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-rf-orange hover:underline font-semibold"
        >
          Google → resubmit sitemap ↗
        </a>
      </div>

      <SitemapLinks byCount={lastResult?.by_sitemap} />

      <p className="text-[11px] text-rf-ink-soft mt-3">
        Google doesn&apos;t accept pings (retired June 2023). It re-reads your sitemap index on its own; after big changes, use “Request indexing” in Search Console for key pages.
      </p>
    </div>
  );
}

const SITEMAP_NAMES = ["recipes", "news", "cookbooks", "directory", "pages"];

function SitemapLinks({ byCount }) {
  const index = "https://recipefiler.com/api/sitemap_index.xml";
  const copy = async () => {
    try { await navigator.clipboard.writeText(index); toast.success("Sitemap index URL copied — paste it into Search Console → Sitemaps"); }
    catch { toast.error("Copy failed — select the URL manually."); }
  };
  return (
    <div className="mt-4 rounded-xl bg-rf-cream-alt/60 p-3" data-testid="admin-sitemap-links">
      <p className="text-xs font-bold text-rf-ink mb-1">Submit this one URL in Google Search Console and Bing Webmaster Tools:</p>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-xs break-all" data-testid="admin-sitemap-index-url">{index}</code>
        <button type="button" onClick={copy} data-testid="admin-sitemap-copy" className="btn-outline-ink !py-1 !px-2.5 text-xs">Copy</button>
      </div>
      <p className="text-[11px] text-rf-ink-soft mt-2">It lists:{" "}
        {SITEMAP_NAMES.map((n, i) => (
          <span key={n}>{i ? " · " : ""}<a href={`/api/sitemap-${n}.xml`} target="_blank" rel="noreferrer" className="text-rf-orange hover:underline">{n}</a>{byCount ? ` (${byCount[n] ?? 0})` : ""}</span>
        ))}
      </p>
    </div>
  );
}
